/**
 * Inline link popover used by text-format controls.
 * Provides URL, Page, Phone, Email, and File link workflows.
 */

import { store } from '../core/state.js';
import { openPopoverMenu } from '../ui/popover-menu.js';
import { escapeHTML as escapeHtml } from '../shared/html-escape.js';
import { pageDestinationOptions, pageForInternalHref } from '../shared/page-routes.js';
import { attachTablistNav } from '../ui/tablist-nav.js';

const TAB_KEYS = ['url', 'page', 'phone', 'email', 'file'];

let popoverEl = null;
let tabEls = null;
let tabNav = null;
let panelEls = null;
let currentTab = 'url';
let currentAnchorRect = null;
let options = null;
let _handle = null;

function normalizeHrefForInput(href) {
  const value = String(href || '').trim();
  if (!value) return '';
  return value;
}

function detectTabFromHref(href) {
  const value = String(href || '')
    .trim()
    .toLowerCase();
  if (!value) return 'url';
  if (value.startsWith('tel:')) return 'phone';
  if (value.startsWith('mailto:')) return 'email';
  if (value.startsWith('pb-asset://')) return 'file';

  const state = store.getState();
  // BC-3: the same matcher the flat exporter resolves links with, so a link
  // the export treats as a page opens on the Page tab, and one it does not
  // (a stale name-only path after a slug change) does not pretend to.
  if (state?.pages?.length && pageForInternalHref(value, state.pages)) return 'page';

  return 'url';
}

function normalizeUrlValue(rawValue) {
  const value = String(rawValue || '').trim();
  if (!value) return '';

  const lower = value.toLowerCase();
  if (
    lower.startsWith('javascript:') ||
    lower.startsWith('data:') ||
    lower.startsWith('vbscript:')
  ) {
    throw new Error('Unsafe URL scheme');
  }

  if (
    value.startsWith('/') ||
    value.startsWith('#') ||
    value.startsWith('./') ||
    value.startsWith('../')
  ) {
    return value;
  }

  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) {
    const protocol = value.slice(0, value.indexOf(':') + 1).toLowerCase();
    const allowed = ['http:', 'https:', 'mailto:', 'tel:', 'pb-asset:'];
    if (!allowed.includes(protocol)) {
      throw new Error('Unsupported URL protocol');
    }
    return value;
  }

  if (/^[^\s]+\.[^\s]+/.test(value)) {
    return `https://${value}`;
  }

  throw new Error('Enter a valid URL');
}

function parsePhoneHref(href) {
  return String(href || '')
    .replace(/^tel:/i, '')
    .trim();
}

function parseEmailHref(href) {
  return String(href || '')
    .replace(/^mailto:/i, '')
    .trim();
}

function parseAssetHref(href) {
  return String(href || '')
    .replace(/^pb-asset:\/\//i, '')
    .trim();
}

// BC-3: page destinations come from shared/page-routes.js — the route the
// exporters resolve, honouring a page's stored slug — not a private slugify.
function buildPageOptions() {
  return pageDestinationOptions(store.getState()?.pages);
}

function setStatusMessage(message, type = 'info') {
  if (!popoverEl) return;
  const statusEl = popoverEl.querySelector('.pb-link-popover-status');
  if (!statusEl) return;
  statusEl.textContent = message || '';
  statusEl.classList.remove('pb-link-popover-status-error');
  if (type === 'error') statusEl.classList.add('pb-link-popover-status-error');
}

function setActiveTab(tab) {
  if (!TAB_KEYS.includes(tab)) return;
  currentTab = tab;

  for (const btn of tabEls) {
    btn.classList.toggle('pb-link-popover-tab-active', btn.dataset.tab === tab);
    btn.setAttribute('aria-selected', btn.dataset.tab === tab ? 'true' : 'false');
  }
  for (const panel of panelEls) {
    const active = panel.dataset.tabPanel === tab;
    panel.hidden = !active;
  }
  tabNav?.sync();

  setStatusMessage('');
}

function getOpenInNewTabInput() {
  return popoverEl?.querySelector('#pb-link-open-in-new-tab') || null;
}

function getCurrentHrefFromInputs() {
  const urlInput = popoverEl.querySelector('#pb-link-url-input');
  const pageSelect = popoverEl.querySelector('#pb-link-page-select');
  const phoneInput = popoverEl.querySelector('#pb-link-phone-input');
  const emailInput = popoverEl.querySelector('#pb-link-email-input');
  const fileSelect = popoverEl.querySelector('#pb-link-file-select');

  switch (currentTab) {
    case 'url':
      return normalizeUrlValue(urlInput?.value || '');
    case 'page': {
      const value = String(pageSelect?.value || '').trim();
      return value;
    }
    case 'phone': {
      const phone = String(phoneInput?.value || '')
        .trim()
        .replace(/^tel:/i, '');
      if (!phone) return '';
      return `tel:${phone}`;
    }
    case 'email': {
      const email = String(emailInput?.value || '')
        .trim()
        .replace(/^mailto:/i, '');
      if (!email) return '';
      return `mailto:${email}`;
    }
    case 'file': {
      const assetId = String(fileSelect?.value || '').trim();
      if (!assetId) return '';
      return `pb-asset://${assetId}`;
    }
    default:
      return '';
  }
}

function updateOpenInNewTabAvailability() {
  const checkbox = getOpenInNewTabInput();
  if (!checkbox) return;
  const tabsAllowingOpenInNewTab = new Set(['url', 'page', 'file']);
  const canToggle = tabsAllowingOpenInNewTab.has(currentTab);
  checkbox.disabled = !canToggle;
  checkbox
    .closest('.pb-link-popover-toggle')
    ?.classList.toggle('pb-link-popover-toggle-disabled', !canToggle);
  if (!canToggle) checkbox.checked = false;
}

function normalizeAnchorRect(anchorRect) {
  if (!anchorRect) return null;
  const left = Number(anchorRect.left || 0);
  const top = Number(anchorRect.top || 0);
  const width = Number(anchorRect.width || 0);
  const height = Number(anchorRect.height || 0);
  return {
    left,
    top,
    width,
    height,
    right: Number(anchorRect.right ?? left + width),
    bottom: Number(anchorRect.bottom ?? top + height)
  };
}

function positionPopover(anchorRect) {
  if (anchorRect) currentAnchorRect = normalizeAnchorRect(anchorRect);
  _handle?.reposition?.();
}

function wireListeners(close) {
  tabEls.forEach((btn) => {
    btn.addEventListener('click', () => {
      setActiveTab(btn.dataset.tab);
      updateOpenInNewTabAvailability();
    });
  });

  popoverEl.querySelector('#pb-link-cancel')?.addEventListener('click', () => {
    close();
  });

  popoverEl.querySelector('#pb-link-unlink')?.addEventListener('click', () => {
    options?.onRemove?.();
    close();
  });

  popoverEl.querySelector('#pb-link-apply')?.addEventListener('click', () => {
    try {
      const href = getCurrentHrefFromInputs();
      if (!href) {
        setStatusMessage('Select or enter a link value.', 'error');
        return;
      }
      const openInNewTab = !!getOpenInNewTabInput()?.checked;
      options?.onApply?.({ href, openInNewTab });
      close();
    } catch (err) {
      setStatusMessage(err?.message || 'Invalid link value', 'error');
    }
  });

  popoverEl.querySelectorAll('input, select').forEach((input) => {
    input.addEventListener('input', () => setStatusMessage(''));
    input.addEventListener('change', () => setStatusMessage(''));
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        popoverEl.querySelector('#pb-link-apply')?.click();
      }
    });
  });
}

async function populateFileTab(initialHref) {
  const fileSelect = popoverEl?.querySelector('#pb-link-file-select');
  if (!fileSelect) return;

  fileSelect.innerHTML = '<option value="">Loading assets…</option>';

  let assets = null;
  try {
    assets = await store.assets.exportAll();
  } catch {
    assets = null;
  }

  if (!assets || assets.size === 0) {
    fileSelect.innerHTML = '<option value="">No uploaded assets</option>';
    return;
  }

  const initialAssetId = parseAssetHref(initialHref);
  const optionsHtml = ['<option value="">Select an uploaded asset</option>'];

  for (const [id, asset] of assets.entries()) {
    const filename = asset?.filename || id;
    const mime = asset?.mime || 'file';
    const selected = id === initialAssetId ? ' selected' : '';
    optionsHtml.push(
      `<option value="${escapeAttr(id)}"${selected}>${escapeHtml(filename)} (${escapeHtml(mime)})</option>`
    );
  }

  fileSelect.innerHTML = optionsHtml.join('');
}

function escapeAttr(value) {
  return escapeHtml(value).replace(/"/g, '&quot;');
}

function renderPopover(currentLink, close, menu) {
  const href = normalizeHrefForInput(currentLink?.href || '');
  const pageOptions = buildPageOptions();
  const initialTab = detectTabFromHref(href);
  const initialOpenInNewTab = String(currentLink?.target || '').toLowerCase() === '_blank';

  const normalizedHref = href
    .replace(/^\/+|\/+$/g, '')
    .replace(/\.html$/, '')
    .toLowerCase();
  const initialPageHref = (() => {
    if (!normalizedHref || normalizedHref === 'index') return '/';
    for (const page of pageOptions) {
      const slug = page.href.replace(/^\/+|\/+$/g, '').toLowerCase();
      if (slug === normalizedHref) return page.href;
    }
    return pageOptions[0]?.href || '/';
  })();

  popoverEl = menu;
  popoverEl.innerHTML = `
    <div class="pb-link-popover-tabs" role="tablist" aria-label="Link type">
      <button class="pb-link-popover-tab" data-tab="url" role="tab" aria-selected="false">URL</button>
      <button class="pb-link-popover-tab" data-tab="page" role="tab" aria-selected="false">Page</button>
      <button class="pb-link-popover-tab" data-tab="phone" role="tab" aria-selected="false">Phone</button>
      <button class="pb-link-popover-tab" data-tab="email" role="tab" aria-selected="false">Email</button>
      <button class="pb-link-popover-tab" data-tab="file" role="tab" aria-selected="false">File</button>
    </div>

    <div class="pb-link-popover-panels">
      <section class="pb-link-popover-panel" data-tab-panel="url">
        <label class="pb-link-popover-label" for="pb-link-url-input">URL</label>
        <input class="pb-link-popover-input" id="pb-link-url-input" type="text" value="${escapeAttr(initialTab === 'url' ? href : '')}" placeholder="https://example.com or /about/" spellcheck="false" />
      </section>

      <section class="pb-link-popover-panel" data-tab-panel="page" hidden>
        <label class="pb-link-popover-label" for="pb-link-page-select">Page</label>
        <select class="pb-link-popover-input" id="pb-link-page-select">
          ${pageOptions.map((page) => `<option value="${escapeAttr(page.href)}"${page.href === initialPageHref ? ' selected' : ''}>${escapeHtml(page.label)}</option>`).join('')}
        </select>
      </section>

      <section class="pb-link-popover-panel" data-tab-panel="phone" hidden>
        <label class="pb-link-popover-label" for="pb-link-phone-input">Phone</label>
        <input class="pb-link-popover-input" id="pb-link-phone-input" type="text" value="${escapeAttr(initialTab === 'phone' ? parsePhoneHref(href) : '')}" placeholder="555-555-5555" spellcheck="false" />
      </section>

      <section class="pb-link-popover-panel" data-tab-panel="email" hidden>
        <label class="pb-link-popover-label" for="pb-link-email-input">Email</label>
        <input class="pb-link-popover-input" id="pb-link-email-input" type="email" value="${escapeAttr(initialTab === 'email' ? parseEmailHref(href) : '')}" placeholder="name@example.com" spellcheck="false" />
      </section>

      <section class="pb-link-popover-panel" data-tab-panel="file" hidden>
        <label class="pb-link-popover-label" for="pb-link-file-select">Uploaded File</label>
        <select class="pb-link-popover-input" id="pb-link-file-select">
          <option value="">Loading assets…</option>
        </select>
      </section>
    </div>

    <div class="pb-link-popover-toggle">
      <label>
        <input type="checkbox" id="pb-link-open-in-new-tab"${initialOpenInNewTab ? ' checked' : ''} />
        Open in new tab
      </label>
    </div>

    <div class="pb-link-popover-status" aria-live="polite"></div>

    <div class="pb-link-popover-actions">
      <button class="pb-link-popover-btn pb-link-popover-btn-muted" id="pb-link-unlink" type="button">Unlink</button>
      <div class="pb-link-popover-actions-right">
        <button class="pb-link-popover-btn pb-link-popover-btn-muted" id="pb-link-cancel" type="button">Cancel</button>
        <button class="pb-link-popover-btn pb-link-popover-btn-primary" id="pb-link-apply" type="button">Apply</button>
      </div>
    </div>
  `;

  tabEls = Array.from(popoverEl.querySelectorAll('.pb-link-popover-tab'));
  panelEls = Array.from(popoverEl.querySelectorAll('.pb-link-popover-panel'));
  // BC-11: arrow keys, Home/End and one tab stop across the five link types.
  tabNav = attachTablistNav(popoverEl.querySelector('.pb-link-popover-tabs'), {
    onActivate: (button) => button.click()
  });

  wireListeners(close);
  setActiveTab(initialTab);
  updateOpenInNewTabAvailability();
  void populateFileTab(href);
}

export function openLinkPopover(nextOptions) {
  const { anchorRect, currentLink = {} } = nextOptions || {};
  if (!anchorRect) return;

  closeLinkPopover();
  options = nextOptions;
  currentAnchorRect = normalizeAnchorRect(anchorRect);
  if (!currentAnchorRect) return;

  _handle = openPopoverMenu(null, {
    owner: 'iframe-text-edit',
    className: 'pb-link-popover',
    useBaseClass: false,
    role: 'dialog',
    menuLabel: 'Edit link',
    align: 'center',
    gap: 10,
    getAnchorRect: () => currentAnchorRect,
    disableArrowNav: true,
    outsideClickFilter: (event) => !!event.target.closest?.('.pb-gp-format-row'),
    consumeCanvasDismiss: false,
    render: ({ close, menu }) => {
      renderPopover(currentLink, close, menu);
      return null;
    },
    restoreFocus: () => {
      if (typeof options?.restoreFocus === 'function') options.restoreFocus();
    },
    onClose: clearStateAfterClose
  });
}

export function positionLinkPopover(anchorRect) {
  positionPopover(anchorRect);
}

function clearStateAfterClose() {
  popoverEl = null;
  tabEls = null;
  tabNav?.destroy();
  tabNav = null;
  panelEls = null;
  currentTab = 'url';
  currentAnchorRect = null;
  _handle = null;

  const closedOptions = options;
  options = null;
  closedOptions?.onClose?.();
}

export function closeLinkPopover() {
  if (_handle) {
    _handle();
    return;
  }
  clearStateAfterClose();
}

export function isLinkPopoverOpen() {
  return !!popoverEl;
}

window.addEventListener(
  'resize',
  () => {
    if (!popoverEl || !currentAnchorRect) return;
    positionPopover(currentAnchorRect);
  },
  { passive: true }
);
